/**
 * Analytics Service
 * Tracks user interactions and custom events for LocalDiff.dev
 * Uses Google Analytics 4 for event tracking and user insights
 */

/**
 * Privacy contract: nothing a user pastes, opens, or fetches may reach GA4.
 * File names, URLs and raw error messages can all contain user content, so
 * only coarse, content-free attributes are transmitted.
 */

/** Lower-case extension without the dot ("" if none), e.g. "report.final.JSON" -> "json". */
export function fileExtension(fileName: string): string {
  const match = /\.([a-z0-9]{1,10})$/i.exec(fileName);
  return match ? match[1].toLowerCase() : '';
}

/** "https" | "http" | "other" — never the host, path or query. */
export function urlScheme(url: string): string {
  const match = /^([a-z][a-z0-9+.-]*):/i.exec(url.trim());
  const scheme = match ? match[1].toLowerCase() : '';
  return scheme === 'https' || scheme === 'http' ? scheme : 'other';
}

/**
 * Error messages may quote user input (e.g. JSON.parse: `"abc" is not valid JSON`).
 * Strip quoted fragments and cap the length before sending.
 */
export function redactErrorMessage(message: string, maxLength = 100): string {
  const redacted = message
    .replace(/"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`/g, '"…"')
    .replace(/\s+/g, ' ')
    .trim();
  return redacted.length > maxLength ? `${redacted.slice(0, maxLength)}…` : redacted;
}

const METRICS_STORAGE_KEY = 'localdiff_metrics';
const LEGACY_METRICS_PREFIX = 'localdiff_metrics_';

interface AnalyticsEvent {
  eventName: string;
  eventData?: Record<string, any>;
  timestamp?: number;
}

interface UserSession {
  sessionId: string;
  userId: string;
  startTime: number;
  endTime?: number;
  comparisonsCount: number;
  totalCharactersProcessed: number;
}

class AnalyticsService {
  private sessionId: string;
  private userId: string;
  private sessionStartTime: number;
  private comparisonsCount: number = 0;
  private totalCharactersProcessed: number = 0;
  private isTrackingEnabled: boolean = true;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.userId = this.getOrCreateUserId();
    this.sessionStartTime = Date.now();
    this.initializeSession();
  }

  /**
   * Initialize analytics session
   */
  private initializeSession(): void {
    if (this.isGAAvailable()) {
      (window as any).dataLayer = (window as any).dataLayer || [];
      // "session_start" is reserved by GA4, so the custom event is namespaced.
      (window as any).gtag?.('event', 'ld_session_start', {
        session_id: this.sessionId,
        user_id: this.userId,
        timestamp: this.sessionStartTime,
      });
    }

    this.removeLegacyMetricKeys();

    // Log session to localStorage for analytics dashboard
    this.logSessionMetric('session_start', {
      sessionId: this.sessionId,
      userId: this.userId,
      timestamp: this.sessionStartTime,
    });
  }

  /**
   * Track comparison event
   */
  trackComparison(params: {
    leftLength: number;
    rightLength: number;
    mode: 'split' | 'unified' | 'merged';
    hasAiAnalysis: boolean;
    timeToCompare?: number;
  }): void {
    if (!this.isTrackingEnabled) return;

    this.comparisonsCount++;
    this.totalCharactersProcessed += params.leftLength + params.rightLength;

    if (this.isGAAvailable()) {
      (window as any).gtag?.('event', 'comparison_made', {
        session_id: this.sessionId,
        user_id: this.userId,
        left_content_length: params.leftLength,
        right_content_length: params.rightLength,
        mode: params.mode,
        has_ai_analysis: params.hasAiAnalysis,
        time_to_compare_ms: params.timeToCompare || 0,
        total_characters: params.leftLength + params.rightLength,
        comparison_count: this.comparisonsCount,
        timestamp: Date.now(),
        engagement_time_msec: Date.now() - this.sessionStartTime,
      });
    }

    this.logSessionMetric('comparison_made', params);
  }

  /**
   * Track file upload event
   */
  trackFileUpload(params: {
    fileName: string;
    fileSize: number;
    fileType: string;
  }): void {
    if (!this.isTrackingEnabled) return;

    if (this.isGAAvailable()) {
      (window as any).gtag?.('event', 'file_upload', {
        session_id: this.sessionId,
        user_id: this.userId,
        file_extension: fileExtension(params.fileName),
        file_size_bytes: params.fileSize,
        file_type: params.fileType,
        timestamp: Date.now(),
      });
    }

    this.logSessionMetric('file_upload', {
      fileExtension: fileExtension(params.fileName),
      fileSize: params.fileSize,
      fileType: params.fileType,
    });
  }

  /**
   * Track URL fetch event
   */
  trackUrlFetch(params: {
    url: string;
    success: boolean;
    responseTime?: number;
  }): void {
    if (!this.isTrackingEnabled) return;

    if (this.isGAAvailable()) {
      (window as any).gtag?.('event', 'url_fetch', {
        session_id: this.sessionId,
        user_id: this.userId,
        url_scheme: urlScheme(params.url),
        success: params.success,
        response_time_ms: params.responseTime || 0,
        timestamp: Date.now(),
      });
    }

    this.logSessionMetric('url_fetch', {
      urlScheme: urlScheme(params.url),
      success: params.success,
      responseTime: params.responseTime,
    });
  }

  /**
   * Track AI analysis/merge operation
   */
  trackAiAnalysis(params: {
    type: 'summarize' | 'merge' | 'explain' | 'feedback';
    inputLength: number;
    success: boolean;
    duration?: number;
  }): void {
    if (!this.isTrackingEnabled) return;

    if (this.isGAAvailable()) {
      (window as any).gtag?.('event', 'ai_analysis', {
        session_id: this.sessionId,
        user_id: this.userId,
        analysis_type: params.type,
        input_length: params.inputLength,
        success: params.success,
        duration_ms: params.duration || 0,
        timestamp: Date.now(),
      });
    }

    this.logSessionMetric('ai_analysis', params);
  }

  /**
   * Track report download
   */
  trackReportDownload(params: {
    format: 'html' | 'json' | 'csv';
    comparisonsInReport: number;
  }): void {
    if (!this.isTrackingEnabled) return;

    if (this.isGAAvailable()) {
      (window as any).gtag?.('event', 'report_download', {
        session_id: this.sessionId,
        user_id: this.userId,
        format: params.format,
        comparisons_count: params.comparisonsInReport,
        timestamp: Date.now(),
      });
    }

    this.logSessionMetric('report_download', params);
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(params: {
    featureName: string;
    action: string;
  }): void {
    if (!this.isTrackingEnabled) return;

    if (this.isGAAvailable()) {
      (window as any).gtag?.('event', 'feature_usage', {
        session_id: this.sessionId,
        user_id: this.userId,
        feature_name: params.featureName,
        action: params.action,
        timestamp: Date.now(),
      });
    }

    this.logSessionMetric('feature_usage', params);
  }

  /**
   * Track error/exception
   */
  trackError(params: {
    errorType: string;
    errorMessage: string;
    stackTrace?: string;
  }): void {
    if (!this.isTrackingEnabled) return;

    if (this.isGAAvailable()) {
      (window as any).gtag?.('event', 'exception', {
        session_id: this.sessionId,
        user_id: this.userId,
        description: `${params.errorType}: ${redactErrorMessage(params.errorMessage)}`,
        fatal: false,
        timestamp: Date.now(),
      });
    }

    this.logSessionMetric('error', {
      errorType: params.errorType,
      errorMessage: redactErrorMessage(params.errorMessage),
    });
  }

  /**
   * Track theme preference
   */
  trackThemePreference(theme: 'light' | 'dark'): void {
    if (!this.isTrackingEnabled) return;

    if (this.isGAAvailable()) {
      (window as any).gtag?.('event', 'theme_preference', {
        session_id: this.sessionId,
        user_id: this.userId,
        theme: theme,
        timestamp: Date.now(),
      });
    }

    this.logSessionMetric('theme_preference', { theme });
  }

  /**
   * Track ad impression
   */
  trackAdImpression(params: {
    adFormat: string;
    adSize?: string;
  }): void {
    if (!this.isTrackingEnabled) return;

    if (this.isGAAvailable()) {
      (window as any).gtag?.('event', 'ad_impression', {
        session_id: this.sessionId,
        user_id: this.userId,
        ad_format: params.adFormat,
        ad_size: params.adSize,
        timestamp: Date.now(),
      });
    }

    this.logSessionMetric('ad_impression', params);
  }

  /**
   * Track affiliate link click
   */
  trackAffiliateClick(params: {
    affiliateId: string;
    affiliate: string;
    category: string;
  }): void {
    if (!this.isTrackingEnabled) return;

    if (this.isGAAvailable()) {
      (window as any).gtag?.('event', 'affiliate_click', {
        session_id: this.sessionId,
        user_id: this.userId,
        affiliate_id: params.affiliateId,
        affiliate: params.affiliate,
        category: params.category,
        timestamp: Date.now(),
      });
    }

    this.logSessionMetric('affiliate_click', params);
  }

  /**
   * End session and log final metrics
   */
  endSession(): void {
    const sessionDuration = Date.now() - this.sessionStartTime;

    if (this.isGAAvailable()) {
      (window as any).gtag?.('event', 'session_end', {
        session_id: this.sessionId,
        user_id: this.userId,
        session_duration_ms: sessionDuration,
        total_comparisons: this.comparisonsCount,
        total_characters_processed: this.totalCharactersProcessed,
        timestamp: Date.now(),
      });
    }

    this.logSessionMetric('session_end', {
      sessionId: this.sessionId,
      sessionDurationMs: sessionDuration,
      comparisonsCount: this.comparisonsCount,
      totalCharactersProcessed: this.totalCharactersProcessed,
    });
  }

  /**
   * Get session analytics summary
   */
  getSessionSummary(): UserSession {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
      startTime: this.sessionStartTime,
      comparisonsCount: this.comparisonsCount,
      totalCharactersProcessed: this.totalCharactersProcessed,
    };
  }

  /**
   * Get user ID or create new one
   */
  private getOrCreateUserId(): string {
    const storageKey = 'localdiff_user_id';
    let userId = localStorage.getItem(storageKey);

    if (!userId) {
      userId = this.generateUserId();
      localStorage.setItem(storageKey, userId);
    }

    return userId;
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate unique user ID (permanent)
   */
  private generateUserId(): string {
    return `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Check if Google Analytics is available
   */
  private isGAAvailable(): boolean {
    return typeof window !== 'undefined' && !!(window as any).gtag;
  }

  /**
   * Log metrics to localStorage for dashboard
   */
  private logSessionMetric(eventType: string, data: any): void {
    try {
      // One rolling key: the old per-session keys were never cleaned up.
      const storageKey = METRICS_STORAGE_KEY;
      let metrics = JSON.parse(localStorage.getItem(storageKey) || '[]');

      if (!Array.isArray(metrics)) {
        metrics = [];
      }

      metrics.push({
        type: eventType,
        sessionId: this.sessionId,
        data,
        timestamp: Date.now(),
      });

      // Keep only last 100 events to prevent localStorage bloat
      if (metrics.length > 100) {
        metrics = metrics.slice(-100);
      }

      localStorage.setItem(storageKey, JSON.stringify(metrics));
    } catch (error) {
      console.warn('[Analytics] Failed to log metric:', error);
    }
  }

  /**
   * Remove per-session metric keys written by earlier versions.
   */
  private removeLegacyMetricKeys(): void {
    try {
      const stale: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(LEGACY_METRICS_PREFIX)) stale.push(key);
      }
      stale.forEach((key) => localStorage.removeItem(key));
    } catch {
      // Storage unavailable (private mode / blocked): nothing to clean.
    }
  }

  /**
   * Enable/disable tracking for privacy
   */
  setTrackingEnabled(enabled: boolean): void {
    this.isTrackingEnabled = enabled;

    if (this.isGAAvailable()) {
      (window as any).gtag?.('consent', 'update', {
        analytics_storage: enabled ? 'granted' : 'denied',
      });
    }
  }

  /**
   * Get tracking status
   */
  isTrackingActive(): boolean {
    return this.isTrackingEnabled;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

// Hook for React component
export const useAnalytics = () => analyticsService;
